import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Search, Download, FileSpreadsheet, FileText, File as FilePdf, ChevronLeft, ChevronRight, QrCode, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import { getReferees, deleteReferee, checkUserCanManageReferees } from '../../services/refereeService';
import { getDepartments } from '../../services/departmentService';
import { Department } from '../../types/models';
import { exportToExcel, exportToCSV, exportToPDF } from '../../utils/export';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';

interface Referee {
  referee_id: string;
  first_name: string;
  last_name: string;
  first_name_tamil?: string;
  last_name_tamil?: string;
  email: string;
  phone?: string;
  is_active: boolean;
  department?: {
    department_id: string;
    name: string;
    name_tamil?: string;
  };
}

interface RefereeAssignment {
  assignment_id: string;
  event_id: string;
  referee_id: string;
  role: string;
  created_at: string;
  event: {
    event_id: string;
    event_name: string;
    reference_number: string;
    status: string;
    category: {
      category_id: string;
      category_name: string;
    };
    age_group?: {
      group_name: string;
    };
  };
}

const ITEMS_PER_PAGE = 10;

function RefereeList() {
  const [referees, setReferees] = useState<Referee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [canManageReferees, setCanManageReferees] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  
  // New state for expanded referee and assignments
  const [expandedRefereeId, setExpandedRefereeId] = useState<string | null>(null);
  const [refereeAssignments, setRefereeAssignments] = useState<RefereeAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedDepartment, selectedStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [refereesData, departmentsData, canManage] = await Promise.all([
        getReferees(),
        getDepartments(),
        checkUserCanManageReferees()
      ]);
      setReferees(refereesData);
      setDepartments(departmentsData);
      setCanManageReferees(canManage);
      setError(null);
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('referees.deleteConfirm.message'))) {
      return;
    }

    try {
      await deleteReferee(id);
      setReferees(referees.filter(referee => referee.referee_id !== id));
      setError(null);
    } catch (err: any) {
      if (err.message?.includes('Cannot delete referee')) {
        setError(t('referees.deleteConfirm.error.hasAssignments'));
      } else {
        setError(err.message || 'Failed to delete referee');
      }
      console.error('Error deleting referee:', err);
    }
  };

  const filteredReferees = referees.filter(referee => {
    const matchesSearch = 
      `${referee.first_name} ${referee.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${referee.first_name_tamil || ''} ${referee.last_name_tamil || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      referee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (referee.phone || '').includes(searchTerm);

    const matchesDepartment = selectedDepartment === 'All' || referee.department?.department_id === selectedDepartment;
    const matchesStatus = selectedStatus === 'All' || 
      (selectedStatus === 'Active' && referee.is_active) || 
      (selectedStatus === 'Inactive' && !referee.is_active);

    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const totalPages = Math.ceil(filteredReferees.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedReferees = filteredReferees.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleExport = (format: 'excel' | 'csv' | 'pdf') => {
    switch (format) {
      case 'excel':
        exportToExcel(filteredReferees);
        break;
      case 'csv':
        exportToCSV(filteredReferees);
        break;
      case 'pdf':
        exportToPDF(filteredReferees);
        break;
    }
    setShowExportMenu(false);
  };

  // New function to toggle referee expansion and fetch assignments
  const toggleRefereeExpansion = async (refereeId: string) => {
    if (expandedRefereeId === refereeId) {
      // If already expanded, collapse it
      setExpandedRefereeId(null);
      setRefereeAssignments([]);
    } else {
      // Otherwise, expand it and fetch assignments
      setExpandedRefereeId(refereeId);
      setLoadingAssignments(true);
      
      try {
        const { data, error } = await supabase
          .from('event_referees')
          .select(`
            assignment_id,
            event_id,
            referee_id,
            role,
            created_at,
            event:events(
              event_id,
              event_name,
              reference_number,
              status,
              category:sports_categories(
                category_id,
                category_name
              ),
              age_group:age_groups(
                group_name
              )
            )
          `)
          .eq('referee_id', refereeId)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        setRefereeAssignments(data || []);
      } catch (err) {
        console.error('Error fetching referee assignments:', err);
        setError('Failed to load referee assignments');
      } finally {
        setLoadingAssignments(false);
      }
    }
  };

  // Helper function to translate role
  const getRoleTranslation = (role: string): string => {
    switch (role) {
      case 'head':
        return t('reports.refereeAssignments.roles.head');
      case 'assistant':
        return t('reports.refereeAssignments.roles.assistant');
      case 'timekeeper':
        return t('reports.refereeAssignments.roles.timekeeper');
      case 'scorer':
        return t('reports.refereeAssignments.roles.scorer');
      default:
        return role;
    }
  };

  // Helper function to get role badge class
  const getRoleClass = (role: string): string => {
    switch (role) {
      case 'head':
        return 'bg-red-100 text-red-800';
      case 'assistant':
        return 'bg-blue-100 text-blue-800';
      case 'timekeeper':
        return 'bg-green-100 text-green-800';
      case 'scorer':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get status badge class
  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'not_started':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('referees.title')}</h1>
        <div className="flex space-x-3">
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="bg-white text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <Download size={20} />
              {t('referees.actions.export')}
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu">
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {t('participants.export.toExcel')}
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {t('participants.export.toCSV')}
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FilePdf className="h-4 w-4 mr-2" />
                    {t('participants.export.toPDF')}
                  </button>
                </div>
              </div>
            )}
          </div>
          <Link
            to="/referees/cards"
            className="bg-white text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <QrCode size={20} />
            Referee Cards
          </Link>
          {user && canManageReferees && (
            <>
              <Link
                to="/referees/import"
                className="bg-white text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <Upload size={20} />
                Import CSV
              </Link>
              <Link
                to="/referees/new"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
              >
                <Plus size={20} />
                {t('referees.actions.addReferee')}
              </Link>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('referees.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('referees.filters.allDepartments')}</option>
                {departments.map(dept => (
                  <option key={dept.department_id} value={dept.department_id}>{dept.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('referees.filters.allStatuses')}</option>
                <option value="Active">{t('referees.filters.active')}</option>
                <option value="Inactive">{t('referees.filters.inactive')}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {paginatedReferees.map((referee) => (
            <div key={referee.referee_id} className="bg-white">
              <div 
                className={`px-6 py-4 cursor-pointer hover:bg-gray-50 ${expandedRefereeId === referee.referee_id ? 'bg-blue-50' : ''}`}
                onClick={() => toggleRefereeExpansion(referee.referee_id)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    {expandedRefereeId === referee.referee_id ? 
                      <ChevronDown className="h-5 w-5 text-blue-500 mr-2" /> : 
                      <ChevronUp className="h-5 w-5 text-gray-400 mr-2" />
                    }
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {referee.first_name} {referee.last_name}
                      </div>
                      {referee.first_name_tamil && referee.last_name_tamil && (
                        <div className="text-sm text-gray-500">
                          {referee.first_name_tamil} {referee.last_name_tamil}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-500">
                      {referee.department?.name || '-'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {referee.email}
                    </div>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      referee.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {referee.is_active ? t('referees.filters.active') : t('referees.filters.inactive')}
                    </span>
                    
                    {user && canManageReferees && (
                      <div className="flex gap-2">
                        <Link
                          to={`/referees/${referee.referee_id}/edit`}
                          className="text-blue-600 hover:text-blue-900"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Edit size={20} />
                        </Link>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(referee.referee_id);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Expanded section with assignments */}
              {expandedRefereeId === referee.referee_id && (
                <div className="border-t border-gray-100 bg-blue-50 px-6 py-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t('referees.assignments.title')}
                  </h3>
                  
                  {loadingAssignments ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  ) : refereeAssignments.length === 0 ? (
                    <div className="text-center py-4 bg-white rounded-lg shadow-sm">
                      <p className="text-gray-500">
                        {t('referees.assignments.noAssignments.title')}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('referees.assignments.table.competition')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('referees.assignments.table.category')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('referees.assignments.table.ageGroup')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('referees.assignments.table.role')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('referees.assignments.table.status')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('referees.assignments.table.reference')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {refereeAssignments.map((assignment) => (
                            <tr key={assignment.assignment_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Link 
                                  to={`/competitions/${assignment.event_id}`}
                                  className="text-blue-600 hover:text-blue-900"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {assignment.event.event_name}
                                </Link>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {assignment.event.category.category_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {assignment.event.age_group?.group_name || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleClass(assignment.role)}`}>
                                  {getRoleTranslation(assignment.role)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(assignment.event.status)}`}>
                                  {t(`competitions.status.${assignment.event.status}`)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                                {assignment.event.reference_number}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(startIndex + ITEMS_PER_PAGE, filteredReferees.length)}
                </span>{' '}
                of <span className="font-medium">{filteredReferees.length}</span> results
              </p>
            </div>
            {totalPages > 1 && (
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RefereeList;